import { IconSparkles } from '@tabler/icons-react'
import type { JSX } from 'react'

interface TimelineNoteSummaryProps {
  aiSummary?: string
  aiSummaryGeneratedAt?: string
  // The AI summary is generated when the note is signed, so unsigned drafts
  // have no summary — pending or otherwise — to preview.
  isSigned: boolean
}

interface SummarySection {
  label: string
  text: string
}

// Section labels the summary generator actually emits at sentence boundaries
// (verified against prod summaries, 2026-07). Restricting the match to these
// keeps ad-hoc colon tokens embedded in a clause (e.g. "CT:", a lab shorthand)
// from being promoted to their own bullet.
const SECTION_RE =
  /(?:^|(?<=[.;]\s))(RFV|Findings?|Plan|PE|Exam|Meds|Refs|Assessment):\s/g

// Splits a summary into labeled sections ("RFV: … Plan: …") for bullet display.
// A section starts at a known label followed by ": " at the string start or
// immediately after a sentence boundary (". " / "; "). Returns [] unless the
// sections cover the whole string from position 0 and there are at least two —
// anything else falls back to the plain blob so no text is ever dropped.
function splitSummarySections(summary: string): SummarySection[] {
  const boundaries = [...summary.matchAll(SECTION_RE)].map((match) => ({
    start: match.index,
    bodyStart: match.index + match[0].length,
    label: match[1],
  }))
  if (boundaries.length < 2 || boundaries[0].start !== 0) {
    return []
  }
  return boundaries.map((boundary, index) => ({
    label: boundary.label,
    text: summary
      .slice(boundary.bodyStart, boundaries[index + 1]?.start ?? summary.length)
      .trim(),
  }))
}

// Renders a note's AI summary as a quiet, quote-style preview beneath its
// timeline entry — a left rule keeps it delineated from the entry line while
// staying borderless so it doesn't break the feed's rhythm, and the label keeps
// the AI provenance explicit. Labeled summaries render one bullet per section
// for scannability; unlabeled ones render as a plain clamped blob. Returns null
// for unsigned notes and once the summary generator has run and produced
// nothing (`aiSummaryGeneratedAt` set with no summary), so the row degrades to
// the plain one-line entry.
export function TimelineNoteSummary({
  aiSummary,
  aiSummaryGeneratedAt,
  isSigned,
}: TimelineNoteSummaryProps): JSX.Element | null {
  const summary = aiSummary?.trim()
  if (!isSigned || (!summary && aiSummaryGeneratedAt)) {
    return null
  }

  const sections = summary ? splitSummarySections(summary) : []

  return (
    <div className="mt-1.5 max-w-(--container-prose) border-l-2 border-border pl-2.5">
      <span className="flex items-center gap-1 text-2xs text-muted-foreground">
        <IconSparkles className="size-3" />
        AI summary
      </span>
      {summary ? (
        sections.length > 0 ? (
          <ul className="mt-0.5 list-disc pl-4">
            {sections.map((section, index) => (
              <li
                // Static list — order never changes after render.
                // oxlint-disable-next-line react/no-array-index-key -- see above
                key={index}
                className="text-xs leading-5 text-foreground-secondary"
              >
                <span className="font-medium">{section.label}:</span>{' '}
                {section.text}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-0.5 line-clamp-3 text-xs leading-5 text-foreground-secondary">
            {summary}
          </p>
        )
      ) : (
        <p className="mt-0.5 text-xs leading-5 text-muted-foreground italic">
          Summary pending…
        </p>
      )}
    </div>
  )
}
